import type { PodcastLiveProvider, PodcastLiveState } from '@/types/podcast';

export class PodcastProviderError extends Error {}

function safeUrl(value: unknown) {
  try {
    const url = new URL(String(value || '').trim());
    if (url.protocol !== 'https:') throw new Error();
    return url;
  } catch {
    throw new PodcastProviderError('Use a valid HTTPS YouTube, Twitch, or Kick URL.');
  }
}

function safeKey(value: string, pattern: RegExp, message: string) {
  const key = value.trim();
  if (!pattern.test(key)) throw new PodcastProviderError(message);
  return key;
}

export function parsePodcastLiveUrl(value: unknown): { provider: PodcastLiveProvider; providerKey: string; canonicalUrl: string } {
  const url = safeUrl(value);
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const parts = url.pathname.split('/').filter(Boolean);
  if (host === 'youtu.be' || host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const id = host === 'youtu.be' ? parts[0] : parts[0] === 'live' || parts[0] === 'embed' ? parts[1] : url.searchParams.get('v') || '';
    const providerKey = safeKey(id || '', /^[A-Za-z0-9_-]{6,20}$/, 'The YouTube URL does not contain a valid video or live-stream ID.');
    return { provider: 'youtube', providerKey, canonicalUrl: `https://www.youtube.com/watch?v=${providerKey}` };
  }
  if (host === 'twitch.tv' || host === 'player.twitch.tv') {
    const video = parts[0] === 'videos' ? safeKey(parts[1] || '', /^\d{3,20}$/, 'The Twitch video ID is invalid.') : url.searchParams.get('video');
    if (video) return { provider: 'twitch', providerKey: `video:v${video.replace(/^v/, '')}`, canonicalUrl: `https://www.twitch.tv/videos/${video.replace(/^v/, '')}` };
    const channel = safeKey(parts[0] || url.searchParams.get('channel') || '', /^[A-Za-z0-9_]{3,25}$/, 'The Twitch channel name is invalid.').toLowerCase();
    return { provider: 'twitch', providerKey: `channel:${channel}`, canonicalUrl: `https://www.twitch.tv/${channel}` };
  }
  if (host === 'kick.com' || host === 'player.kick.com') {
    const channel = safeKey(parts[0] || '', /^[A-Za-z0-9_-]{2,40}$/, 'The Kick channel name is invalid.').toLowerCase();
    return { provider: 'kick', providerKey: channel, canonicalUrl: `https://kick.com/${channel}` };
  }
  throw new PodcastProviderError('Only YouTube, Twitch, and Kick live URLs are allowed.');
}

export function podcastEmbedUrl(provider: PodcastLiveProvider, providerKey: string, parentHost: string) {
  if (provider === 'youtube') return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(providerKey)}?autoplay=1`;
  if (provider === 'kick') return `https://player.kick.com/${encodeURIComponent(providerKey)}`;
  const [kind, value] = providerKey.split(':', 2);
  const params = new URLSearchParams({ parent: parentHost.replace(/^www\./, '') });
  params.set(kind === 'video' ? 'video' : 'channel', value);
  return `https://player.twitch.tv/?${params.toString()}`;
}

export function podcastLiveState(event: { startAt: number; endAt: number }, now = Date.now()): PodcastLiveState {
  if (now < event.startAt) return 'upcoming';
  if (now <= event.endAt) return 'live';
  return 'ended';
}
